import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, addWeeks, format, isWithinInterval } from "date-fns";

const getDisplayName = (user) => {
  if (user.first_name || user.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user.full_name || user.email;
};
import { Calendar, TrendingUp } from "lucide-react";

export default function WorkloadAnalysis({ users, tasks, timeEntries }) {
  const [viewMode, setViewMode] = useState("week"); // "week" or "month"
  // Generate 8 weeks of data (4 past + current + 3 future)
  const weeks = [];
  for (let i = -4; i <= 3; i++) {
    const weekStart = startOfWeek(addWeeks(new Date(), i), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(addWeeks(new Date(), i), { weekStartsOn: 1 });
    weeks.push({ start: weekStart, end: weekEnd, label: format(weekStart, "d MMM") });
  }

  // Calculate workload per week
  const weeklyData = weeks.map(week => {
    const weekTasks = tasks.filter(task => {
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return isWithinInterval(dueDate, { start: week.start, end: week.end });
    });

    const weekTimeEntries = timeEntries.filter(entry => {
      const entryDate = new Date(entry.start_time);
      return isWithinInterval(entryDate, { start: week.start, end: week.end });
    });

    const estimatedHours = weekTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    const actualHours = weekTimeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);

    return {
      week: week.label,
      Estimert: parseFloat(estimatedHours.toFixed(1)),
      Faktisk: parseFloat(actualHours.toFixed(1)),
      Oppgaver: weekTasks.length,
    };
  });

  // Current period analysis (week or month)
  const periodStart = viewMode === "week" 
    ? startOfWeek(new Date(), { weekStartsOn: 1 })
    : startOfMonth(new Date());
  const periodEnd = viewMode === "week"
    ? endOfWeek(new Date(), { weekStartsOn: 1 })
    : endOfMonth(new Date());

  const userWorkloads = users.map(user => {
    const periodTasks = tasks.filter(task => {
      if (task.assigned_to !== user.email) return false;
      if (task.status === "completed" || task.status === "cancelled") return false;
      if (!task.due_date) return false;
      const dueDate = new Date(task.due_date);
      return isWithinInterval(dueDate, { start: periodStart, end: periodEnd });
    });

    const periodTimeEntries = timeEntries.filter(entry => {
      if (entry.user_email !== user.email) return false;
      const entryDate = new Date(entry.start_time);
      return isWithinInterval(entryDate, { start: periodStart, end: periodEnd });
    });

    const estimatedHours = periodTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    const actualHours = periodTimeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
    const capacity = viewMode === "week" 
      ? (user.weekly_capacity || 37.5)
      : ((user.weekly_capacity || 37.5) * 4.33); // Approximately 4.33 weeks per month

    return {
      name: getDisplayName(user),
      estimatedHours: parseFloat(estimatedHours.toFixed(1)),
      actualHours: parseFloat(actualHours.toFixed(1)),
      capacity,
      utilization: parseFloat(((actualHours / capacity) * 100).toFixed(1)),
      taskCount: periodTasks.length,
    };
  }).sort((a, b) => b.actualHours - a.actualHours);

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === "week" ? "default" : "outline"}
          onClick={() => setViewMode("week")}
          className="flex items-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          Denne Uken
        </Button>
        <Button
          variant={viewMode === "month" ? "default" : "outline"}
          onClick={() => setViewMode("month")}
          className="flex items-center gap-2"
        >
          <TrendingUp className="w-4 h-4" />
          Denne Måneden
        </Button>
      </div>

      {/* Workload Trend */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Arbeidsmengde Over Tid</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={weeklyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="week" stroke="#64748b" />
            <YAxis stroke="#64748b" label={{ value: 'Timer', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="Estimert" stroke="#f59e0b" strokeWidth={2} />
            <Line type="monotone" dataKey="Faktisk" stroke="#10b981" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Current Period Workload */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          {viewMode === "week" 
            ? `Denne Uken - ${format(periodStart, "d MMM")} til ${format(periodEnd, "d MMM")}`
            : `Denne Måneden - ${format(periodStart, "MMM yyyy")}`
          }
        </h3>
        <div className="space-y-3">
          {userWorkloads.map(user => (
            <div key={user.name} className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-slate-900">{user.name}</p>
                  <p className="text-sm text-slate-500">
                    {user.taskCount} oppgaver {viewMode === "week" ? "denne uken" : "denne måneden"}
                  </p>
                </div>
                <Badge className={
                  user.utilization > 100 ? "bg-rose-100 text-rose-700" :
                  user.utilization > 80 ? "bg-amber-100 text-amber-700" :
                  "bg-emerald-100 text-emerald-700"
                }>
                  {user.utilization}% utnyttelse
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Estimert</p>
                  <p className="font-semibold text-amber-600">{user.estimatedHours}h</p>
                </div>
                <div>
                  <p className="text-slate-500">Faktisk</p>
                  <p className="font-semibold text-emerald-600">{user.actualHours}h</p>
                </div>
                <div>
                  <p className="text-slate-500">Kapasitet</p>
                  <p className="font-semibold text-slate-900">{user.capacity}h</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}